import React from 'react'
import { CircularProgress, Tooltip } from '@mui/material'
import PeopleAltIcon from '@mui/icons-material/PeopleAltRounded'
import { keyframes } from '@mui/system'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import useListenerCountStore from '../../store/listenerCount'
import useStationListenerCountSettingsStore from '../../store/stationListenerCounts'
import { getListenerCount } from '../../api/station'
import { withListenerDebug, withListenerDebugAsync } from '../../utils/listenerDebug'
import {
  LISTENER_COUNT_REFRESH_INTERVAL_MS,
  resolveListenerCountStale,
  formatListenerCountRelativeTime,
} from '../../utils/listenerCount'
import { HeaderIconButton } from './HeaderIconButton'

type FetchContext = 'initial' | 'manual' | 'auto' | 'visibility' | 'version'

const flashAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.6); }
  60% { box-shadow: 0 0 0 12px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`

const warningFlashAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 167, 38, 0.6); }
  60% { box-shadow: 0 0 0 12px rgba(255, 167, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 167, 38, 0); }
`

export interface ListenerCountButtonProps {
  stationId?: number | null
}

const ListenerCountButton: React.FC<ListenerCountButtonProps> = ({ stationId }) => {
  const theme = useTheme()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const { enabled, count, lastUpdated, stale, version, loading, hasFetched, error } =
    useListenerCountStore((state) => ({
      enabled: state.enabled,
      count: state.count,
      lastUpdated: state.lastUpdated,
      stale: state.stale,
      version: state.version,
      loading: state.loading,
      hasFetched: state.hasFetched,
      error: state.error,
    }))
  const setListenerCountState = useListenerCountStore((state) => state.setState)
  const resetListenerCount = useListenerCountStore((state) => state.reset)
  const listenerSettings = useStationListenerCountSettingsStore(
    React.useCallback(
      (state) => (stationId ? state.listenerCounts[stationId] : undefined),
      [stationId],
    ),
  )
  const setStationListenerCountSettings = useStationListenerCountSettingsStore(
    (state) => state.setListenerCountSettings,
  )

  const [animate, setAnimate] = React.useState(false)
  const timerRef = React.useRef<number | null>(null)
  const nextFetchRef = React.useRef<number | null>(null)
  const previousCountRef = React.useRef<number | null>(null)
  const fetchListenerCountRef = React.useRef<((force: boolean) => Promise<void>) | null>(null)
  const remainingDelayRef = React.useRef<number | null>(null)
  const activeRef = React.useRef(false)
  const fetchContextRef = React.useRef<FetchContext>('initial')

  const clearTimer = React.useCallback(
    (options?: { preserveRemaining?: boolean }) => {
      const hadTimer = timerRef.current !== null
      const preserveRemaining = Boolean(options?.preserveRemaining)
      const nextFetchAt = nextFetchRef.current

      if (hadTimer || preserveRemaining) {
      withListenerDebug('timer:clear', (log) => {
        log.info('Clearing listener count timer', {
          hadTimer,
          preserveRemaining,
          nextFetchAt: nextFetchAt ? new Date(nextFetchAt).toISOString() : null,
          remainingMs: nextFetchAt ? Math.max(0, nextFetchAt - Date.now()) : null,
        })
      })
    }

    if (hadTimer && timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }

      if (!preserveRemaining) {
        nextFetchRef.current = null
        remainingDelayRef.current = null
      }
      setListenerCountState({ nextRefreshAt: null })
    },
    [setListenerCountState],
  )

  const scheduleFetch = React.useCallback(
    (delay: number) => {
      clearTimer()

      const requestedDelay = delay

      if (!activeRef.current) {
        withListenerDebug('timer:schedule', (log) => {
          log.info('Skipped scheduling listener count fetch', {
            reason: 'inactive',
            requestedDelayMs: requestedDelay,
          })
        })
        return
      }

      const callback = fetchListenerCountRef.current

      if (!callback) {
        withListenerDebug('timer:schedule', (log) => {
          log.warn('Fetch callback missing; cannot schedule listener count refresh', {
            requestedDelayMs: requestedDelay,
          })
        })
        return
      }

      const normalizedDelay = Number.isFinite(requestedDelay) && requestedDelay > 0 ? requestedDelay : 0
      const nextAt = Date.now() + normalizedDelay

      withListenerDebug('timer:schedule', (log) => {
        log.info('Scheduling listener count fetch', {
          delayMs: normalizedDelay,
          nextFetchAt: new Date(nextAt).toISOString(),
        })
      })

      timerRef.current = window.setTimeout(() => {
        if (activeRef.current) {
          fetchContextRef.current = 'auto'
          void callback(false)
        }
      }, normalizedDelay)
      nextFetchRef.current = nextAt
      remainingDelayRef.current = normalizedDelay
      setListenerCountState({ nextRefreshAt: nextAt })
    },
    [clearTimer, setListenerCountState],
  )

  const resetState = React.useCallback(() => {
    clearTimer()
    withListenerDebug('state:reset', (log) => {
      log.info('Resetting listener count state', { stationId })
    })
    previousCountRef.current = null
    setListenerCountState({
      enabled: false,
      count: null,
      lastUpdated: null,
      stale: null,
      loading: false,
      hasFetched: false,
      error: null,
      nextRefreshAt: null,
    })
  }, [clearTimer, setListenerCountState, stationId])

  const applyResponse = React.useCallback(
    (response: Awaited<ReturnType<typeof getListenerCount>>) => {
      const previousCount = previousCountRef.current
      const effectiveStale = resolveListenerCountStale(response.stale, response.last_updated)

      withListenerDebug('fetch:apply-response', (log) => {
        log.info('Applying listener count response', {
          enabled: response.enabled,
          previousCount,
          nextCount: response.count,
          changed: response.count !== previousCount,
          effectiveStale,
          lastUpdated: response.last_updated,
          cache: response.cache ?? null,
          http_status: response.http_status ?? null,
        })
      })

      setListenerCountState({
        enabled: response.enabled,
        count: response.count,
        lastUpdated: response.last_updated,
        stale: effectiveStale,
        error: null,
        hasFetched: true,
        loading: false,
      })

      if (stationId && !response.enabled) {
        setStationListenerCountSettings(stationId, { enabled: false, endpoint: null })
      }

      if (response.enabled && activeRef.current) {
        scheduleFetch(LISTENER_COUNT_REFRESH_INTERVAL_MS)
      } else {
        clearTimer()
      }

      if (response.count !== null && response.count !== previousCountRef.current) {
        if (!prefersReducedMotion) {
          setAnimate(true)
        }
        previousCountRef.current = response.count
      } else if (response.count === null) {
        previousCountRef.current = response.count
      }
    },
    [
      clearTimer,
      resolveListenerCountStale,
      prefersReducedMotion,
      scheduleFetch,
      setListenerCountState,
      setStationListenerCountSettings,
      stationId,
    ],
  )

  const fetchListenerCount = React.useCallback(
    async (force: boolean) => {
      const context = fetchContextRef.current

      if (!stationId || !activeRef.current) {
        withListenerDebug('fetch:skipped', (log) => {
          log.info('Skipped listener count fetch', {
            stationId,
            force,
            context,
            active: activeRef.current,
          })
        })
        fetchContextRef.current = 'auto'
        return
      }

      setListenerCountState({ loading: true })

      try {
        await withListenerDebugAsync(
          `fetch station=${stationId} force=${force ? '1' : '0'} context=${context}`,
          async (log) => {
            log.info('Starting listener count fetch', {
              stationId,
              force,
              context,
            })

            try {
              const response = await getListenerCount(stationId, { force })
              const previousCount = previousCountRef.current
              log.info('Listener count fetch complete', {
                enabled: response.enabled,
                count: response.count,
                last_updated: response.last_updated,
                stale: response.stale,
                cache: response.cache ?? null,
                http_status: response.http_status ?? null,
                previousCount,
              })

              if (response.count !== previousCount) {
                log.info('Listener count changed', {
                  previous: previousCount,
                  next: response.count,
                })
              }

              applyResponse(response)
            } catch (error) {
              log.error('Listener count fetch failed', error)
              throw error
            }
          },
        )
      } catch (error) {
        setListenerCountState({ hasFetched: true, error: { occurredAt: Date.now() } })
        const shouldRetry = useListenerCountStore.getState().enabled && activeRef.current
        withListenerDebug('fetch:recover', (log) => {
          log.warn('Recovering from listener count fetch error', {
            stationId,
            context,
            force,
            shouldRetry,
          })
        })
        if (useListenerCountStore.getState().enabled && activeRef.current) {
          scheduleFetch(LISTENER_COUNT_REFRESH_INTERVAL_MS)
        } else {
          clearTimer()
        }
      } finally {
        setListenerCountState({ loading: false })
        fetchContextRef.current = 'auto'
      }
    },
    [applyResponse, clearTimer, scheduleFetch, stationId],
  )

  fetchListenerCountRef.current = fetchListenerCount

  const configLoaded = Boolean(listenerSettings?.loaded)
  const configEnabled = Boolean(listenerSettings?.enabled)
  const configEndpoint = listenerSettings?.endpoint ?? null
  const active = Boolean(configLoaded && configEnabled && configEndpoint)

  React.useEffect(() => {
    activeRef.current = active
    if (!active) {
      clearTimer()
    }
  }, [active, clearTimer])

  React.useEffect(() => {
    const nextFetchAt = nextFetchRef.current
    const resolved = resolveListenerCountStale(stale, lastUpdated)

    withListenerDebug('state:snapshot', (log) => {
      log.info('Listener count state snapshot', {
        stationId,
        active,
        config: {
          loaded: configLoaded,
          enabled: configEnabled,
          endpoint: configEndpoint,
        },
        store: {
          enabled,
          count,
          lastUpdated,
          stale,
          resolvedStale: resolved,
        },
        timers: {
          hasTimer: Boolean(timerRef.current),
          nextFetchAt: nextFetchAt ? new Date(nextFetchAt).toISOString() : null,
          remainingMs: nextFetchAt ? Math.max(0, nextFetchAt - Date.now()) : null,
        },
        loading,
        hasFetched,
        fetchError: error ? { occurredAt: error.occurredAt } : null,
      })
    })
  }, [
    active,
    configEnabled,
    configEndpoint,
    configLoaded,
    resolveListenerCountStale,
    count,
    enabled,
    error,
    hasFetched,
    lastUpdated,
    loading,
    stale,
    stationId,
  ])

  React.useEffect(() => {
    if (!stationId) {
      resetState()
      return () => {}
    }

    if (!configLoaded) {
      resetState()
      return () => {}
    }

    if (!active) {
      resetState()
      return () => {}
    }

    setListenerCountState({
      enabled: true,
      count: null,
      lastUpdated: null,
      stale: null,
      hasFetched: false,
      error: null,
    })
    previousCountRef.current = null

    fetchContextRef.current = 'initial'
    void fetchListenerCount(true)

    return () => {
      clearTimer()
    }
  }, [
    active,
    clearTimer,
    configLoaded,
    fetchListenerCount,
    resetState,
    setListenerCountState,
    stationId,
  ])

  React.useEffect(() => {
    if (!stationId || !active) {
      return
    }

    if (version > 0) {
      fetchContextRef.current = 'version'
      void fetchListenerCount(true)
    }
  }, [active, fetchListenerCount, stationId, version])

  React.useEffect(() => {
    if (!animate) {
      return
    }

    const timeout = window.setTimeout(() => {
      setAnimate(false)
    }, 800)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [animate])

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (nextFetchRef.current) {
          const remaining = nextFetchRef.current - Date.now()
          remainingDelayRef.current = remaining > 0 ? remaining : 0
        }
        clearTimer({ preserveRemaining: true })
        nextFetchRef.current = null
        return
      }

      if (!stationId || !activeRef.current) {
        return
      }

      const remaining = remainingDelayRef.current ?? (nextFetchRef.current ? nextFetchRef.current - Date.now() : null)
      remainingDelayRef.current = null

      if (remaining === null || remaining <= 0) {
        fetchContextRef.current = 'visibility'
        void fetchListenerCount(false)
      } else {
        scheduleFetch(remaining)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearTimer, fetchListenerCount, scheduleFetch, stationId])

  React.useEffect(() => () => {
    clearTimer()
  }, [clearTimer])

  React.useEffect(() => () => {
    resetListenerCount()
  }, [resetListenerCount])

  if (!stationId || !active) {
    return null
  }

  const resolvedStale = resolveListenerCountStale(stale, lastUpdated)
  const badgeColor = resolvedStale ? theme.palette.warning.main : theme.palette.success.main
  const badgeTextColor = '#ffffff'
  const badgeClassName = React.useMemo(
    () => `BadgePill ${resolvedStale ? 'BadgePill--listenerStale' : 'BadgePill--listener'}`,
    [resolvedStale],
  )
  const badgeStyle = React.useMemo<React.CSSProperties>(
    () =>
      (resolvedStale
        ? {
            '--badge-warning-bg': badgeColor,
            '--badge-warning-fg': badgeTextColor,
          }
        : {
            '--badge-success-bg': badgeColor,
            '--badge-success-fg': badgeTextColor,
          }) as React.CSSProperties,
    [badgeColor, badgeTextColor, resolvedStale],
  )
  const buttonStyle = React.useMemo<React.CSSProperties | undefined>(() => {
    if (!animate) {
      return undefined
    }

    if (prefersReducedMotion) {
      return { animation: 'none' }
    }

    const animation = resolvedStale ? warningFlashAnimation : flashAnimation
    return { animation: `${animation} 800ms ease-out` }
  }, [animate, prefersReducedMotion, resolvedStale])
  const lastUpdatedRelative = lastUpdated ? formatListenerCountRelativeTime(lastUpdated) : null
  let tooltipLabel: string

  if (error) {
    tooltipLabel = lastUpdatedRelative
      ? `Update failed — last updated ${lastUpdatedRelative}`
      : 'Update failed — last updated unknown'
  } else if (lastUpdatedRelative) {
    tooltipLabel = `Listener count — last updated ${lastUpdatedRelative}`
  } else if (hasFetched) {
    tooltipLabel = 'Listener count — not yet loaded'
  } else {
    tooltipLabel = 'Listener count — loading…'
  }
  const badgeContent = count ?? '–'

  return (
    <Tooltip title={tooltipLabel} disableInteractive>
      <span>
        <HeaderIconButton
          type="button"
          onClick={() => {
            if (!stationId || !activeRef.current) {
              withListenerDebug('fetch:manual-trigger', (log) => {
                log.warn('Manual refresh skipped', {
                  stationId,
                  active: activeRef.current,
                })
              })
              return
            }
            withListenerDebug('fetch:manual-trigger', (log) => {
              log.info('Manual refresh requested', { stationId })
            })
            fetchContextRef.current = 'manual'
            void fetchListenerCount(true)
          }}
          disabled={loading}
          aria-label="Refresh listener count"
          style={buttonStyle}
        >
          <PeopleAltIcon />
          <span className="HeaderBadge">
            <span className={badgeClassName} style={badgeStyle} aria-live="polite">
              {loading ? <CircularProgress size={14} color="inherit" thickness={5} /> : badgeContent}
            </span>
          </span>
        </HeaderIconButton>
      </span>
    </Tooltip>
  )
}

export default ListenerCountButton
